import apiClient from './client';
import { Country } from './countries';

export interface Currency {
  id: string;
  code: string;
  name: string;
}

export interface ProviderCurrencyBankDetails {
  accountName?: string | null;
  accountNumber?: string | null;
  bankName?: string | null;
  branchCode?: string | null;
  bankCode?: string | null;
  bankLocation?: string | null;
  bankAddress?: string | null;
  swiftCode?: string | null;
}

export interface ProviderCurrency {
  id: string;
  currencyId: string;
  currency: string;
  bankDetails?: ProviderCurrencyBankDetails | null;
}

/// New per-currency input shape for create/update.
export interface ProviderCurrencyInput {
  currencyId: string;
  bankDetails?: ProviderCurrencyBankDetails;
  /** Optional opening balance, only used on create */
  initialBalance?: number;
}

/** Per-provider OUTGOING_PAYMENT_FEE (stored in providers.metadata.outgoingFee). Null/undefined = server global defaults. */
export interface OutgoingPaymentFeeSettings {
  currencyId?: string;
  mode: string;
  fixedAmount: number;
  fixedFeeCurrencyId?: string;
  percentOfAmount: number;
  minFee: number;
  maxFee: number;
}

/** Per-provider INCOMING_PAYMENT_FEE (stored in providers.metadata.incomingFee). Same shape as outgoing. */
export type IncomingPaymentFeeSettings = OutgoingPaymentFeeSettings;

/** Per-provider INTERNAL_TRANSFER fee (stored in providers.metadata.internalFee). Same shape as outgoing. */
export type InternalPaymentFeeSettings = OutgoingPaymentFeeSettings;

/** Per-provider Digital Outgoing payment fee (stored in providers.metadata.digitalOutgoingFee). */
export type DigitalOutgoingPaymentFeeSettings = OutgoingPaymentFeeSettings;

/** Per-provider Digital Incoming payment fee (stored in providers.metadata.digitalIncomingFee). */
export type DigitalIncomingPaymentFeeSettings = OutgoingPaymentFeeSettings;

export interface Provider {
  id?: string;
  name: string;
  countryId: string;
  country?: Country;
  currencies: ProviderCurrency[];
  status?: string;
  accountName?: string;
  accountNumber?: string;
  bankName?: string;
  branchCode?: string;
  bankCode?: string;
  bankLocation?: string;
  bankAddress?: string;
  swiftCode?: string;
  outgoingPaymentFee?: OutgoingPaymentFeeSettings | null;
  outgoingPaymentFeesByCurrency?: Record<string, OutgoingPaymentFeeSettings>;
  incomingPaymentFee?: IncomingPaymentFeeSettings | null;
  incomingPaymentFeesByCurrency?: Record<string, IncomingPaymentFeeSettings>;
  internalPaymentFee?: InternalPaymentFeeSettings | null;
  internalPaymentFeesByCurrency?: Record<string, InternalPaymentFeeSettings>;
  digitalOutgoingPaymentFee?: DigitalOutgoingPaymentFeeSettings | null;
  digitalOutgoingPaymentFeesByCurrency?: Record<string, DigitalOutgoingPaymentFeeSettings>;
  digitalIncomingPaymentFee?: DigitalIncomingPaymentFeeSettings | null;
  digitalIncomingPaymentFeesByCurrency?: Record<string, DigitalIncomingPaymentFeeSettings>;
}

export type OutgoingPaymentFeeUpdatePayload = {
  currencyId?: string;
  mode: string;
  fixedAmount: number;
  fixedFeeCurrencyId?: string;
  percentOfAmount: number;
  minFee: number;
  maxFee: number;
};

export type IncomingPaymentFeeUpdatePayload = OutgoingPaymentFeeUpdatePayload;
export type InternalPaymentFeeUpdatePayload = OutgoingPaymentFeeUpdatePayload;
export type DigitalOutgoingPaymentFeeUpdatePayload = OutgoingPaymentFeeUpdatePayload;
export type DigitalIncomingPaymentFeeUpdatePayload = OutgoingPaymentFeeUpdatePayload;

export interface ProviderCurrencyInitialBalance {
  currencyId: string;
  initialBalance?: number | null;
}

export interface CreateProviderRequest {
  countryId: string;
  name: string;
  /** New per-currency format. When provided, this is the source of truth (banking + opening balance per currency). */
  currencies: ProviderCurrencyInput[];
}

export interface UpdateProviderRequest {
  countryId: string;
  name: string;
  currencies: ProviderCurrencyInput[];
}

export interface ProviderLedgerAccountRow {
  accountId: string;
  currencyId: string;
  accountTypeCode: string;
  balance: number;
}

export interface ProviderCurrencyTotal {
  currencyId: string;
  currencyCode?: string;
  totalBalance: number;
}

export interface ProviderClientAccount {
  id: string;
  clientId: string;
  clientFullName: string;
  clientPublicId?: string;
  currencyId: string;
  currencyCode?: string;
  status: string;
  accountName?: string;
  accountNumber?: string;
  availableBalance: number;
  blockedBalance: number;
  totalBalance: number;
}

export interface ProviderViewDetails {
  provider: Provider;
  ledgerAccounts: ProviderLedgerAccountRow[];
  totalsByCurrency: ProviderCurrencyTotal[];
  clientAccounts: ProviderClientAccount[];
  integration?: ProviderIntegration | null;
  problemEventCount: number;
}

export interface ProviderRequestLog {
  id: string;
  operation: string;
  businessObjectType: string;
  businessObjectId?: string | null;
  businessKey?: string | null;
  requestId: string;
  correlationId?: string | null;
  httpStatusCode?: number | null;
  providerErrorCode?: string | null;
  durationMs: number;
  outcome: string;
  createdAt: string;
}

export interface ProviderRequestLogPayload {
  id: string;
  requestPayload?: string | null;
  responsePayload?: string | null;
}

export interface ProviderRequestLogsPage {
  items: ProviderRequestLog[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

function mapBankDetails(raw: unknown): ProviderCurrencyBankDetails | null {
  if (!raw) return null;
  const x = raw as Record<string, unknown>;
  const get = (a: string, b: string) => (x[a] ?? x[b]) as string | null | undefined;
  return {
    accountName: get('accountName', 'AccountName'),
    accountNumber: get('accountNumber', 'AccountNumber'),
    bankName: get('bankName', 'BankName'),
    branchCode: get('branchCode', 'BranchCode'),
    bankCode: get('bankCode', 'BankCode'),
    bankLocation: get('bankLocation', 'BankLocation'),
    bankAddress: get('bankAddress', 'BankAddress'),
    swiftCode: get('swiftCode', 'SwiftCode'),
  };
}

function mapOutgoingPaymentFee(raw: unknown): OutgoingPaymentFeeSettings | null {
  if (raw == null) return null;
  const x = raw as Record<string, unknown>;
  const n = (a: string, b: string) => Number(x[a] ?? x[b] ?? 0);
  const modeRaw = String(x.mode ?? x.Mode ?? 'fixed').toLowerCase();
  const mode =
    modeRaw === 'percent' || modeRaw === 'percentage' ? 'percent' : 'fixed';
  return {
    currencyId: (x.currencyId ?? x.CurrencyId) as string | undefined,
    mode,
    fixedAmount: n('fixedAmount', 'FixedAmount'),
    fixedFeeCurrencyId: String(x.fixedFeeCurrencyId ?? x.FixedFeeCurrencyId ?? '').trim().toUpperCase() || undefined,
    percentOfAmount: n('percentOfAmount', 'PercentOfAmount'),
    minFee: n('minFee', 'MinFee'),
    maxFee: n('maxFee', 'MaxFee'),
  };
}

function mapFeeMap(raw: unknown): Record<string, OutgoingPaymentFeeSettings> {
  if (!raw || typeof raw !== 'object') return {};
  const source = raw as Record<string, unknown>;
  const mapped: Record<string, OutgoingPaymentFeeSettings> = {};
  for (const [key, value] of Object.entries(source)) {
    const fee = mapOutgoingPaymentFee(value);
    if (!fee) continue;
    mapped[key.toUpperCase()] = { ...fee, currencyId: key };
  }
  return mapped;
}

function mapViewDetailsProvider(raw: unknown): Provider {
  const p = raw as Record<string, unknown>;
  const cur = (p.currencies ?? p.Currencies ?? []) as Record<string, unknown>[];
  const feeRaw = p.outgoingPaymentFee ?? p.OutgoingPaymentFee;
  const inFeeRaw = p.incomingPaymentFee ?? p.IncomingPaymentFee;
  const outByCurrencyRaw = p.outgoingPaymentFeesByCurrency ?? p.OutgoingPaymentFeesByCurrency;
  const inByCurrencyRaw = p.incomingPaymentFeesByCurrency ?? p.IncomingPaymentFeesByCurrency;
  const internalFeeRaw = p.internalPaymentFee ?? p.InternalPaymentFee;
  const internalByCurrencyRaw = p.internalPaymentFeesByCurrency ?? p.InternalPaymentFeesByCurrency;
  const digitalOutFeeRaw = p.digitalOutgoingPaymentFee ?? p.DigitalOutgoingPaymentFee;
  const digitalOutByCurrencyRaw = p.digitalOutgoingPaymentFeesByCurrency ?? p.DigitalOutgoingPaymentFeesByCurrency;
  const digitalInFeeRaw = p.digitalIncomingPaymentFee ?? p.DigitalIncomingPaymentFee;
  const digitalInByCurrencyRaw = p.digitalIncomingPaymentFeesByCurrency ?? p.DigitalIncomingPaymentFeesByCurrency;
  return {
    id: String(p.id ?? p.Id ?? ''),
    name: String(p.name ?? p.Name ?? ''),
    countryId: String(p.countryId ?? p.CountryId ?? ''),
    status: (p.status ?? p.Status) as string | undefined,
    accountName: (p.accountName ?? p.AccountName) as string | undefined,
    accountNumber: (p.accountNumber ?? p.AccountNumber) as string | undefined,
    bankName: (p.bankName ?? p.BankName) as string | undefined,
    branchCode: (p.branchCode ?? p.BranchCode) as string | undefined,
    bankCode: (p.bankCode ?? p.BankCode) as string | undefined,
    bankLocation: (p.bankLocation ?? p.BankLocation) as string | undefined,
    bankAddress: (p.bankAddress ?? p.BankAddress) as string | undefined,
    swiftCode: (p.swiftCode ?? p.SwiftCode) as string | undefined,
    outgoingPaymentFee: feeRaw != null ? mapOutgoingPaymentFee(feeRaw) ?? null : null,
    outgoingPaymentFeesByCurrency: mapFeeMap(outByCurrencyRaw),
    incomingPaymentFee: inFeeRaw != null ? mapOutgoingPaymentFee(inFeeRaw) ?? null : null,
    incomingPaymentFeesByCurrency: mapFeeMap(inByCurrencyRaw),
    internalPaymentFee: internalFeeRaw != null ? mapOutgoingPaymentFee(internalFeeRaw) ?? null : null,
    internalPaymentFeesByCurrency: mapFeeMap(internalByCurrencyRaw),
    digitalOutgoingPaymentFee: digitalOutFeeRaw != null ? mapOutgoingPaymentFee(digitalOutFeeRaw) ?? null : null,
    digitalOutgoingPaymentFeesByCurrency: mapFeeMap(digitalOutByCurrencyRaw),
    digitalIncomingPaymentFee: digitalInFeeRaw != null ? mapOutgoingPaymentFee(digitalInFeeRaw) ?? null : null,
    digitalIncomingPaymentFeesByCurrency: mapFeeMap(digitalInByCurrencyRaw),
    currencies: cur.map((x) => ({
      id: String(x.id ?? x.Id ?? ''),
      currencyId: String(x.currencyId ?? x.CurrencyId ?? ''),
      currency: String(x.currency ?? x.Currency ?? ''),
      bankDetails: mapBankDetails(x.bankDetails ?? x.BankDetails),
    })),
  };
}

export const providersApi = {
  getAll: async (): Promise<Provider[]> => {
    const response = await apiClient.get('/Provider');
    const list = (response.data.data || response.data) as unknown[];
    return Array.isArray(list) ? list.map((row) => mapViewDetailsProvider(row)) : [];
  },

  getById: async (id: string): Promise<Provider> => {
    const response = await apiClient.get(`/Provider/get-by-id?id=${id}`);
    const raw = (response.data?.data ?? response.data) as unknown;
    return mapViewDetailsProvider(raw);
  },

  getViewDetails: async (id: string): Promise<ProviderViewDetails> => {
    const response = await apiClient.get(`/Provider/${id}/view-details`);
    const raw = response.data as Record<string, unknown>;
    const la = (raw.ledgerAccounts ?? raw.LedgerAccounts ?? []) as Record<string, unknown>[];
    const tt = (raw.totalsByCurrency ?? raw.TotalsByCurrency ?? []) as Record<string, unknown>[];
    const ca = (raw.clientAccounts ?? raw.ClientAccounts ?? []) as Record<string, unknown>[];
    return {
      provider: mapViewDetailsProvider(raw.provider ?? raw.Provider),
      ledgerAccounts: la.map((r) => ({
        accountId: String(r.accountId ?? r.AccountId ?? ''),
        currencyId: String(r.currencyId ?? r.CurrencyId ?? ''),
        accountTypeCode: String(r.accountTypeCode ?? r.AccountTypeCode ?? ''),
        balance: Number(r.balance ?? r.Balance ?? 0),
      })),
      totalsByCurrency: tt.map((t) => ({
        currencyId: String(t.currencyId ?? t.CurrencyId ?? ''),
        currencyCode: (t.currencyCode ?? t.CurrencyCode) as string | undefined,
        totalBalance: Number(t.totalBalance ?? t.TotalBalance ?? 0),
      })),
      clientAccounts: ca.map((c) => ({
        id: String(c.id ?? c.Id ?? ''),
        clientId: String(c.clientId ?? c.ClientId ?? ''),
        clientFullName: String(c.clientFullName ?? c.ClientFullName ?? ''),
        clientPublicId: (c.clientPublicId ?? c.ClientPublicId) as string | undefined,
        currencyId: String(c.currencyId ?? c.CurrencyId ?? ''),
        currencyCode: (c.currencyCode ?? c.CurrencyCode) as string | undefined,
        status: String(c.status ?? c.Status ?? ''),
        accountName: (c.accountName ?? c.AccountName) as string | undefined,
        accountNumber: (c.accountNumber ?? c.AccountNumber) as string | undefined,
        availableBalance: Number(c.availableBalance ?? c.AvailableBalance ?? 0),
        blockedBalance: Number(c.blockedBalance ?? c.BlockedBalance ?? 0),
        totalBalance: Number(c.totalBalance ?? c.TotalBalance ?? 0),
      })),
      integration: (raw.integration ?? raw.Integration)
        ? {
            type: ((((raw.integration ?? raw.Integration) as Record<string, unknown>).type
              ?? ((raw.integration ?? raw.Integration) as Record<string, unknown>).Type
              ?? null) as ProviderIntegrationType),
            unitId: ((((raw.integration ?? raw.Integration) as Record<string, unknown>).unitId
              ?? ((raw.integration ?? raw.Integration) as Record<string, unknown>).UnitId
              ?? null) as string | null),
          }
        : null,
      problemEventCount: Number(raw.problemEventCount ?? raw.ProblemEventCount ?? 0),
    };
  },

  create: async (data: CreateProviderRequest): Promise<Provider> => {
    const response = await apiClient.post('/Provider', data);
    return response.data;
  },

  update: async (data: UpdateProviderRequest & { id: string }): Promise<Provider> => {
    const response = await apiClient.put(`/Provider/update?id=${data.id}`, data);
    return response.data;
  },

  changeStatus: async (id: string, isActive: boolean): Promise<void> => {
    await apiClient.patch(`/Provider/${id}/status`, { isActive });
  },

  archive: async (id: string): Promise<void> => {
    await apiClient.delete(`/Provider/${id}`);
  },

  updateOutgoingPaymentFee: async (id: string, payload: OutgoingPaymentFeeUpdatePayload): Promise<void> => {
    await apiClient.put(`/Provider/${id}/outgoing-payment-fee`, payload);
  },

  updateIncomingPaymentFee: async (id: string, payload: IncomingPaymentFeeUpdatePayload): Promise<void> => {
    await apiClient.put(`/Provider/${id}/incoming-payment-fee`, payload);
  },

  updateInternalPaymentFee: async (id: string, payload: InternalPaymentFeeUpdatePayload): Promise<void> => {
    await apiClient.put(`/Provider/${id}/internal-payment-fee`, payload);
  },

  updateDigitalOutgoingPaymentFee: async (id: string, payload: DigitalOutgoingPaymentFeeUpdatePayload): Promise<void> => {
    await apiClient.put(`/Provider/${id}/digital-outgoing-payment-fee`, payload);
  },

  updateDigitalIncomingPaymentFee: async (id: string, payload: DigitalIncomingPaymentFeeUpdatePayload): Promise<void> => {
    await apiClient.put(`/Provider/${id}/digital-incoming-payment-fee`, payload);
  },

  getIntegration: async (id: string): Promise<ProviderIntegration | null> => {
    const response = await apiClient.get(`/Provider/${id}/integration`);
    const raw = response.data;
    if (!raw) return null;
    return {
      type: (raw.type ?? raw.Type ?? null) as ProviderIntegrationType,
      unitId: (raw.unitId ?? raw.UnitId ?? null) as string | null,
    };
  },

  updateIntegration: async (id: string, payload: ProviderIntegrationUpdatePayload): Promise<void> => {
    await apiClient.put(`/Provider/${id}/integration`, payload);
  },

  getRequestLogs: async (
    id: string,
    params: {
      operation?: string;
      outcome?: string;
      from?: string;
      to?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ): Promise<ProviderRequestLogsPage> => {
    const response = await apiClient.get(`/Provider/${id}/request-logs`, { params });
    const raw = response.data as Record<string, unknown>;
    const rows = (raw.items ?? raw.Items ?? []) as Record<string, unknown>[];
    return {
      items: rows.map((row) => ({
        id: String(row.id ?? row.Id ?? ''),
        operation: String(row.operation ?? row.Operation ?? ''),
        businessObjectType: String(row.businessObjectType ?? row.BusinessObjectType ?? ''),
        businessObjectId: (row.businessObjectId ?? row.BusinessObjectId) as string | null | undefined,
        businessKey: (row.businessKey ?? row.BusinessKey) as string | null | undefined,
        requestId: String(row.requestId ?? row.RequestId ?? ''),
        correlationId: (row.correlationId ?? row.CorrelationId) as string | null | undefined,
        httpStatusCode: Number(row.httpStatusCode ?? row.HttpStatusCode ?? 0) || null,
        providerErrorCode: (row.providerErrorCode ?? row.ProviderErrorCode) as string | null | undefined,
        durationMs: Number(row.durationMs ?? row.DurationMs ?? 0),
        outcome: String(row.outcome ?? row.Outcome ?? ''),
        createdAt: String(row.createdAt ?? row.CreatedAt ?? ''),
      })),
      totalCount: Number(raw.totalCount ?? raw.TotalCount ?? 0),
      page: Number(raw.page ?? raw.Page ?? 1),
      pageSize: Number(raw.pageSize ?? raw.PageSize ?? 25),
      totalPages: Number(raw.totalPages ?? raw.TotalPages ?? 0),
      hasNextPage: Boolean(raw.hasNextPage ?? raw.HasNextPage),
      hasPreviousPage: Boolean(raw.hasPreviousPage ?? raw.HasPreviousPage),
    };
  },

  getRequestLogPayload: async (id: string, logId: string): Promise<ProviderRequestLogPayload> => {
    const response = await apiClient.get(`/Provider/${id}/request-logs/${logId}/payload`);
    const raw = response.data as Record<string, unknown>;
    return {
      id: String(raw.id ?? raw.Id ?? logId),
      requestPayload: (raw.requestPayload ?? raw.RequestPayload) as string | null | undefined,
      responsePayload: (raw.responsePayload ?? raw.ResponsePayload) as string | null | undefined,
    };
  },
};

export type ProviderIntegrationType = 'PYVIO' | null;

export interface ProviderIntegration {
  type: ProviderIntegrationType;
  unitId: string | null;
}

export interface ProviderIntegrationUpdatePayload {
  type: ProviderIntegrationType;
  unitId?: string | null;
}

export interface ProviderFeeRule {
  id: string;
  providerId: string;
  operationType: 'OUTGOING_PAYMENT' | 'INCOMING_PAYMENT' | 'INTERNAL_TRANSFER' | 'DIGITAL_OUTGOING' | 'DIGITAL_INCOMING' | 'FX' | 'RETURN';
  paymentMethod?: 'SWIFT' | 'SEPA' | 'INTERNAL' | 'DIGITAL' | null;
  paymentCurrencyId?: string | null;
  feeCurrencyId?: string | null;
  calculationMode: 'FIXED' | 'PERCENT' | 'PERCENT_PLUS_FIXED';
  fixedAmount: number;
  percentOfAmount: number;
  minFee: number;
  maxFee: number;
  priority: number;
  isActive: boolean;
  isExplicitZero: boolean;
  companyCompensatesShortfall: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  description?: string | null;
}

export interface ProviderFeeSnapshot {
  id: string;
  businessObjectType: string;
  businessObjectId: string;
  operationType: string;
  expectedFeeAmount: number;
  actualFeeAmount?: number | null;
  feeCurrencyId: string;
  actualFeeCurrencyId?: string | null;
  actualFeeSourceEventId?: string | null;
  compensationAmount: number;
  companyCompensatesShortfall: boolean;
  settlementStatus: string;
  exceptionReason?: string | null;
  resolution?: 'POST_ACTUAL' | 'POST_EXPECTED' | 'WAIVE' | null;
  resolvedByActorId?: string | null;
  resolvedAt?: string | null;
  updatedAt: string;
}

export interface ProviderCostRule {
  id: string;
  providerId: string;
  feeCurrencyCode: string;
  operationType: 'INCOMING' | 'OUTGOING';
  paymentMethod: 'ANY' | 'SEPA' | 'SWIFT';
  currencyCode: string;
  calculationMode: 'FIXED' | 'PERCENTAGE' | 'PERCENTAGE_PLUS_FIXED';
  fixedAmount: number;
  percentOfAmount: number;
  minFee: number;
  maxFee: number;
  priority: number;
  isActive: boolean;
  effectiveFrom?: string;
  effectiveTo?: string | null;
}

export const providerFeeRulesApi = {
  list: async (providerId: string): Promise<ProviderFeeRule[]> => {
    const response = await apiClient.get(`/Provider/${providerId}/fee-rules`);
    return response.data.data ?? response.data;
  },
  create: async (providerId: string, rule: Omit<ProviderFeeRule, 'id' | 'providerId'>): Promise<ProviderFeeRule> => {
    const response = await apiClient.post(`/Provider/${providerId}/fee-rules`, rule);
    return response.data;
  },
  update: async (providerId: string, ruleId: string, rule: Omit<ProviderFeeRule, 'id' | 'providerId'>): Promise<ProviderFeeRule> => {
    const response = await apiClient.put(`/Provider/${providerId}/fee-rules/${ruleId}`, rule);
    return response.data;
  },
  deactivate: async (providerId: string, ruleId: string): Promise<void> => {
    await apiClient.delete(`/Provider/${providerId}/fee-rules/${ruleId}`);
  },
  snapshots: async (providerId: string): Promise<ProviderFeeSnapshot[]> => {
    const response = await apiClient.get(`/Provider/${providerId}/fee-snapshots`);
    return response.data.data ?? response.data;
  },
  resolveSnapshot: async (
    providerId: string,
    snapshotId: string,
    resolution: 'POST_ACTUAL' | 'POST_EXPECTED' | 'WAIVE',
  ): Promise<ProviderFeeSnapshot> => {
    const response = await apiClient.post(
      `/Provider/${providerId}/fee-snapshots/${snapshotId}/resolve`,
      { resolution },
    );
    return response.data.data ?? response.data;
  },
};

export const providerCostRulesApi = {
  list: async (providerId: string): Promise<ProviderCostRule[]> => {
    const response = await apiClient.get(`/providers/${providerId}/fee-rules`);
    return response.data.data ?? response.data;
  },
  create: async (providerId: string, rule: Omit<ProviderCostRule, 'id' | 'providerId'>): Promise<ProviderCostRule> => {
    const response = await apiClient.post(`/providers/${providerId}/fee-rules`, rule);
    return response.data;
  },
  update: async (providerId: string, ruleId: string, rule: Omit<ProviderCostRule, 'id' | 'providerId'>): Promise<ProviderCostRule> => {
    const response = await apiClient.put(`/providers/${providerId}/fee-rules/${ruleId}`, rule);
    return response.data;
  },
};

export interface ProviderRouting {
  id?: string;
  clientId: string;
  currencyId: string;
  providerId: string;
  priority: number;
  isActive: boolean;
  clientName?: string;
  currencyCode?: string;
  providerName?: string;
}

export interface CreateProviderRoutingRequest {
  clientId: string;
  currencyId: string;
  providerId: string;
  priority: number;
  isActive: boolean;
}

export const currenciesApi = {
  getAll: async (): Promise<Currency[]> => {
    const response = await apiClient.get('/Currency');
    return response.data.data || response.data;
  },
};

export const providerRoutingApi = {
  getAll: async (): Promise<ProviderRouting[]> => {
    const response = await apiClient.get('/Provider/get-provider-routing-list');
    return response.data.data || response.data;
  },

  getById: async (id: string): Promise<ProviderRouting> => {
    const response = await apiClient.get(`/Provider/get-provider-routing-by-id?id=${id}`);
    return response.data;
  },

  create: async (data: CreateProviderRoutingRequest): Promise<ProviderRouting> => {
    const response = await apiClient.post('/Provider/add-provider-routing', data);
    return response.data;
  },

  update: async (id: string, data: CreateProviderRoutingRequest): Promise<ProviderRouting> => {
    const response = await apiClient.put(`/Provider/update-provider-routing?id=${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/Provider/delete-provider-routing/${id}`);
  },
};
